import React, { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { useWidgetTodoOverviewPreviewQuery } from "./useWidgetTodoOverviewPreviewQuery";
import { WATodoCategoryPreview } from "./WidgetTodoOverviewPreview";
import { WSTodoOverviewMutation } from "./__generated__/WSTodoOverviewMutation.graphql";
import { WSTodoOverviewQuery } from "./__generated__/WSTodoOverviewQuery.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetTodoOverviewSettings } from "../WidgetTodoOverviewSettings/WidgetTodoOverviewSettings";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";
import { WidgetScreen } from "../../common/WidgetScreen";

export type WSTodoOverviewParams = object;

const WS_TODO_OVERVIEW_QUERY = graphql`
  query WSTodoOverviewQuery($id: ID!) {
    store {
      ...WidgetTodoOverviewSettings_Store
      publicTeams {
        id
      }
    }
    widgetTodoOverview(id: $id) {
      id
      category {
        id
      }
      displayMode
      teams {
        id
      }
    }
  }
`;

export const WSTodoOverview = withWebModal(WSTodoOverviewC, true);
function WSTodoOverviewC(
  props: UserLoggedInNavigatorScreenProps<"WSTodoOverview">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerWSTodoOverview {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWSTodoOverview(
  props: UserLoggedInNavigatorScreenProps<"WSTodoOverview"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WSTodoOverviewQuery>(
    WS_TODO_OVERVIEW_QUERY,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const remoteDisplayMode = data.widgetTodoOverview?.displayMode ?? "MEDIUM";
  const [displayMode, setDisplayMode] = useState<"MEDIUM" | "MEDIUM_GROUPED">(
    remoteDisplayMode
  );
  useEffect(() => {
    setDisplayMode(remoteDisplayMode);
  }, [remoteDisplayMode]);

  const remoteTeamIds = useMemo(
    () => data.widgetTodoOverview?.teams.map(({ id }) => id) ?? [],
    [data.widgetTodoOverview?.teams]
  );
  const [teamIds, setTeamIds] = useState<string[]>(remoteTeamIds);
  useEffect(() => {
    setTeamIds(remoteTeamIds);
  }, [remoteTeamIds]);

  const [save, loading] = useMutation<WSTodoOverviewMutation>(graphql`
    mutation WSTodoOverviewMutation(
      $storecastId: ID!
      $widgetId: ID!
      $update: WidgetTodoOverviewUpdateInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetTodoOverview(id: $widgetId, update: $update) {
          id
          ...WidgetTodoOverview_widget
          displayMode
          teams {
            id
            name
          }
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    save({
      variables: {
        storecastId: props.route.params.storecastId,
        update: {
          setTeamIds: teamIds,
          setDisplayMode: displayMode,
        },
        widgetId: props.route.params.widgetId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    displayMode,
    props.route.params.widgetId,
    save,
    teamIds,
  ]);

  const query = useWidgetTodoOverviewPreviewQuery({
    teamIds,
    categoryId: data.widgetTodoOverview?.category?.id ?? "",
    storecastId: props.route.params.storecastId,
    displayMode,
  });

  return (
    <WidgetScreen
      settings={
        <WidgetTodoOverviewSettings
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          store={data.store}
          setTeamIds={setTeamIds}
          teamIds={teamIds}
        />
      }
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          size={props.route.params.size}
          unitSize={props.route.params.unitSize}
        >
          {query ? <WATodoCategoryPreview query={query} /> : null}
        </WidgetScreenPreview>
      }
    />
  );
}
